import React, { useEffect, useState } from 'react';
import { Button, Intent, Tooltip } from '@blueprintjs/core';
import DataSyncList from './components/DataSyncList';
import { useHistory } from 'react-router-dom';
import MainContainer from './MainContainer';
import { directoryStore } from '../../stores/DirectoryStore';
import { DataSyncStore, dataSyncStore } from '../../stores/DataSyncStore';
import { dataSyncMetricsStore } from '../../stores/DataSyncMetricsStore';
import { MetricsGranularity } from '../../lib/metricsUtils';
import { showToasts } from '../../lib/toaster';
import { useObserver } from 'mobx-react';
import { reaction } from 'mobx';

const DAILY_GRANULARITY: MetricsGranularity = 'METRICS_GRANULARITY_DAILY';
const DAY_IN_SECONDS = 24 * 60 * 60;
const LIST_METRICS_RANGE_DAYS = 7;

const getLastNDaysRange = (days: number) => {
  const toEpoch = Math.floor(Date.now() / 1000);
  const fromEpoch = toEpoch - ((days - 1) * DAY_IN_SECONDS);
  return { fromEpoch, toEpoch };
};

interface ListMetricsQuery {
  trackerId: string;
  fromEpoch: number;
  toEpoch: number;
  granularity: MetricsGranularity;
}

export const DataSyncListPage = () => {
  const history = useHistory();
  const [metricsQuery, setMetricsQuery] = useState<ListMetricsQuery | undefined>(undefined);

  const fetchDataSyncs = async () => {
    try {
      if (directoryStore.trackerId) {
        await dataSyncStore.fetch();
      }
    } catch (e) {
      showToasts([{intent: 'danger', message: `Failed to fetch data syncs. ${e}`}]);
    }
  };

  useEffect(() => {
    fetchDataSyncs();
  }, []);

  useEffect(() => {
    const disposer = reaction(
      () => ({
        trackerId: directoryStore.trackerId,
        loading: dataSyncStore.loading,
        rowStart: dataSyncStore.paging.rowStart,
        rowCount: dataSyncStore.paging.rowCount,
        searchFilter: dataSyncStore.searchFilter,
        filterByStatus: dataSyncStore.filterByStatus,
        sortField: dataSyncStore.sort.field,
        sortDirection: dataSyncStore.sort.direction,
        visibleIds: dataSyncStore.dataSyncs.map((item) => item.id),
      }),
      async ({ trackerId, loading, visibleIds }) => {
        if (!trackerId || loading || visibleIds.length === 0) {
          return;
        }

        try {
          const { fromEpoch, toEpoch } = getLastNDaysRange(LIST_METRICS_RANGE_DAYS);
          const query: ListMetricsQuery = {
            trackerId,
            fromEpoch,
            toEpoch,
            granularity: DAILY_GRANULARITY,
          };
          setMetricsQuery(query);
          await dataSyncMetricsStore.fetchMetrics(trackerId, visibleIds, {
            fromEpoch,
            toEpoch,
            granularity: DAILY_GRANULARITY,
          });
        } catch (e) {
          showToasts([{ intent: 'danger', message: `Failed to fetch metrics. ${e}` }]);
        }
      },
      { fireImmediately: true }
    );

    return () => {
      disposer();
    };
  }, []);

  const headerActions = useObserver(() => (
    <>
      <Button
        text="Refresh"
        intent={Intent.NONE}
        onClick={async () => {
          await fetchDataSyncs();
        }}
      />
      <Tooltip
        content={`Maximum number of data syncs is ${DataSyncStore.MAX_DATA_SYNCS}. Please delete existing syncs before creating a new one.`}
        disabled={dataSyncStore.dataSyncCount < DataSyncStore.MAX_DATA_SYNCS}
        placement="bottom"
      >
        <Button
          disabled={dataSyncStore.dataSyncCount >= DataSyncStore.MAX_DATA_SYNCS}
          intent={Intent.PRIMARY}
          text="New Sync"
          onClick={() => history.push('/data_syncs/new')}
        />
      </Tooltip>
    </>
  ));

  return (
    <MainContainer headerActions={headerActions} fixedHeight={true}>
      <DataSyncList
        dataSyncStore={dataSyncStore}
        metricsStore={dataSyncMetricsStore}
        metricsQuery={metricsQuery}
      />
    </MainContainer>
  );
};
